import {
  CHANNELS_SUPPORTING_BREAKDOWN,
  CHANNELS_WITHOUT_BREAKDOWN_SUPPORT,
} from "../constants.ts";
import type { FilterTypesEnum } from "../filter-types.ts";
import type {
  Granularity,
  KpiBreakdown,
  KpiBreakdownForChannelResponse,
} from "../kpi-api-types.ts";
import type { DateScopeOverrideInput } from "./date-scope.ts";
import {
  getTracifyApiJson,
  type TracifyApiBasePath,
} from "./tracify-api-client.ts";
import {
  parseOptionalMetricKey,
  parseRequiredChannelKey,
  validateMetricKeysList,
} from "./metrics.ts";
import {
  type AnalyticsScope,
  createAnalyticsScopeFromTracifyContext,
} from "./scope.ts";
import type { TracifyContext } from "../ai/request-context.ts";

type BreakdownSupportKey = keyof typeof CHANNELS_SUPPORTING_BREAKDOWN;

export type BreakdownFilterInput =
  | string
  | Array<{ name: string; operator: FilterTypesEnum; value: unknown }>
  | null;

export type ChannelKpiBreakdownFetchInput = {
  channel: string;
  breakdown: KpiBreakdown;
  granularity?: Granularity | null;
  requested_metrics?: string[] | null;
  filters?: BreakdownFilterInput;
  offset?: number | null;
  limit?: number | null;
  sortBy?: string | null;
  sortOrder?: "asc" | "desc" | null;
};

export type FetchChannelKpiBreakdownOptions = {
  baseUrl: string;
  scope: AnalyticsScope;
  input: ChannelKpiBreakdownFetchInput;
  token: string;
  timeoutMs?: number;
  maxRetries?: number;
  retryDelayMs?: number;
  requestId?: string;
  apiBasePath?: TracifyApiBasePath;
};

export type FetchChannelKpiBreakdownWithContextOptions = Omit<
  FetchChannelKpiBreakdownOptions,
  "scope" | "input"
> & {
  context: TracifyContext;
  input: ChannelKpiBreakdownFetchInput & DateScopeOverrideInput;
};

export const CHANNEL_KPI_BREAKDOWN_DEFAULT_LIMIT = 25;
export const CHANNEL_KPI_BREAKDOWN_MAX_LIMIT = 50;

const DEFAULT_GRANULARITY: Granularity = "none";

const breakdownToSupportKey: Partial<Record<KpiBreakdown, BreakdownSupportKey>> =
  {
    advertising_channel_type: "channel",
    campaign: "campaign",
    adset: "adset",
    ad: "ad",
    keyword: "keyword",
    asset_group: "asset_group",
    creative: "creative",
  };

function appendGranularity(
  searchParams: URLSearchParams,
  granularity: Granularity
) {
  searchParams.append("granularity", granularity);
}

function normalizeMetrics(requestedMetrics: string[] | null | undefined) {
  const validated = validateMetricKeysList(
    requestedMetrics,
    "requested_metrics"
  );
  return validated ? [...validated].sort() : null;
}

export function clampChannelKpiBreakdownLimit(
  limit: number | null | undefined,
  defaultLimit: number
): number {
  if (limit === null || limit === undefined || Number.isNaN(limit)) {
    return defaultLimit;
  }

  return Math.min(
    Math.max(Math.trunc(limit), 1),
    CHANNEL_KPI_BREAKDOWN_MAX_LIMIT
  );
}

export function validateChannelKpiBreakdownSupport(
  channel: string,
  breakdown: KpiBreakdown
) {
  if (breakdown === "advertising_channel_type") {
    return;
  }

  const supportKey = breakdownToSupportKey[breakdown];
  if (!supportKey) {
    throw new Error(`Breakdown '${breakdown}' is not supported yet.`);
  }
  const supportedChannels = CHANNELS_SUPPORTING_BREAKDOWN[supportKey];

  if (supportedChannels === null) {
    return;
  }

  if (CHANNELS_WITHOUT_BREAKDOWN_SUPPORT.includes(channel as any)) {
    throw new Error(
      `Channel '${channel}' does not support breakdown '${breakdown}'.`
    );
  }

  if (!supportedChannels.includes(channel as any)) {
    throw new Error(
      `Breakdown '${breakdown}' is not supported for channel '${channel}'.`
    );
  }
}

function normalizeOffset(offset: number | null | undefined): number | null {
  if (offset === null || offset === undefined || Number.isNaN(offset)) {
    return null;
  }

  return Math.max(Math.trunc(offset), 0);
}

function appendFilters(searchParams: URLSearchParams, filters: string | null) {
  if (filters) {
    searchParams.append("filters", filters);
  }
}

function normalizeFilters(
  filters: ChannelKpiBreakdownFetchInput["filters"],
  scopeFilters: AnalyticsScope["filters"]
): string | null {
  if (filters === null || filters === undefined) {
    return scopeFilters ?? null;
  }

  if (typeof filters === "string") {
    const trimmed = filters.trim();
    return trimmed.length > 0 ? trimmed : null;
  }

  if (Array.isArray(filters) && filters.length === 0) {
    return null;
  }

  try {
    return JSON.stringify(filters);
  } catch {
    return null;
  }
}

function appendCustomerTarget(
  searchParams: URLSearchParams,
  scope: AnalyticsScope
) {
  if (scope.customerType && scope.customerType !== "total") {
    searchParams.append("filter_and_sort_target", scope.customerType);
  }
}

function appendComparison(
  searchParams: URLSearchParams,
  comparison: { start: string; end: string } | null
) {
  if (!comparison) {
    return;
  }

  searchParams.append("comparison_start_date", comparison.start);
  searchParams.append("comparison_end_date", comparison.end);
}

export function buildChannelKpiBreakdownRequest(
  scope: AnalyticsScope,
  input: ChannelKpiBreakdownFetchInput
): { path: string; searchParams: URLSearchParams } {
  const granularity = input.granularity ?? DEFAULT_GRANULARITY;
  const channel = parseRequiredChannelKey(input.channel, "channel");
  const requestedMetrics = normalizeMetrics(input.requested_metrics);
  const filters = normalizeFilters(input.filters, scope.filters);
  const offset = normalizeOffset(input.offset);
  const defaultLimit = clampChannelKpiBreakdownLimit(
    scope.topN ?? CHANNEL_KPI_BREAKDOWN_DEFAULT_LIMIT,
    CHANNEL_KPI_BREAKDOWN_DEFAULT_LIMIT
  );
  const limit = clampChannelKpiBreakdownLimit(input.limit, defaultLimit);
  const sortBy =
    parseOptionalMetricKey(input.sortBy ?? null, "sort_by") ??
    parseOptionalMetricKey(scope.rankBy ?? null, "scope.rank_by");

  validateChannelKpiBreakdownSupport(channel, input.breakdown);

  const searchParams = new URLSearchParams();
  searchParams.append("preset_id", scope.presetId);
  searchParams.append("start_date", scope.startDate);
  searchParams.append("end_date", scope.endDate);

  for (const csid of scope.storeIds) {
    searchParams.append("csids", csid);
  }

  if (requestedMetrics) {
    for (const metric of requestedMetrics) {
      searchParams.append("requested_metrics", metric);
      searchParams.append("granularity_only_metrics", metric);
    }
  }

  appendGranularity(searchParams, granularity);
  appendFilters(searchParams, filters);
  appendCustomerTarget(searchParams, scope);
  appendComparison(searchParams, scope.comparison);

  if (offset !== null) {
    searchParams.append("offset", offset.toString());
  }

  searchParams.append("limit", limit.toString());

  if (sortBy) {
    searchParams.append("sort_by", sortBy);
  }

  if (input.sortOrder) {
    searchParams.append("descending", `${input.sortOrder === "desc"}`);
  }

  return {
    path: `/analytics/kpis/channels/${channel}/${input.breakdown}`,
    searchParams,
  };
}

export async function fetchChannelKpiBreakdownWithScope({
  baseUrl,
  scope,
  input,
  token,
  timeoutMs,
  maxRetries,
  retryDelayMs,
  requestId,
  apiBasePath,
}: FetchChannelKpiBreakdownOptions): Promise<KpiBreakdownForChannelResponse> {
  const request = buildChannelKpiBreakdownRequest(scope, input);

  return getTracifyApiJson<KpiBreakdownForChannelResponse>({
    baseUrl,
    path: request.path,
    searchParams: request.searchParams,
    token,
    resource: `KPI breakdown for channel '${input.channel}'`,
    timeoutMs,
    maxRetries,
    retryDelayMs,
    requestId,
    apiBasePath,
  });
}

export async function fetchChannelKpiBreakdownWithContext({
  context,
  input,
  ...options
}: FetchChannelKpiBreakdownWithContextOptions): Promise<KpiBreakdownForChannelResponse> {
  return fetchChannelKpiBreakdownWithScope({
    ...options,
    scope: createAnalyticsScopeFromTracifyContext(context, input),
    input,
  });
}
